import { useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle, LayersControl, LayerGroup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './ShopMap.css';

const { BaseLayer } = LayersControl;

// Fix for default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom shop icon (larger, different color)
const shopIcon = new L.DivIcon({
  className: 'shop-marker-icon',
  html: `<div class="shop-marker">
    <svg viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
    </svg>
  </div>`,
  iconSize: [40, 40],
  iconAnchor: [20, 40],
  popupAnchor: [0, -40],
});

// Custom address icon (small dot)
const createAddressIcon = (type) => {
  let colorClass = 'address-dot-new';

  if (type === 'batch') {
    colorClass = 'address-dot-batch';
  } else if (type === 'used') {
    colorClass = 'address-dot-used';
  }

  return new L.DivIcon({
    className: 'address-marker-icon',
    html: `<div class="address-dot ${colorClass}"></div>`,
    iconSize: [12, 12],
    iconAnchor: [6, 6],
    popupAnchor: [0, -6],
  });
};

// Existing-customer icon (slightly larger ringed dot, distinct color)
const customerIcon = new L.DivIcon({
  className: 'customer-marker-icon',
  html: `<div class="customer-map-dot"></div>`,
  iconSize: [16, 16],
  iconAnchor: [8, 8],
  popupAnchor: [0, -8],
});

// Draggable "search area" handle (the movable center of the coverage circle).
// The shop marker itself stays FIXED; this is what the user drags.
const searchAreaIcon = new L.DivIcon({
  className: 'search-area-marker-icon',
  html: `<div class="search-area-handle">
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
      <circle cx="12" cy="12" r="3"/>
      <path d="M12 2v4M12 18v4M2 12h4M18 12h4"/>
    </svg>
  </div>`,
  iconSize: [30, 30],
  iconAnchor: [15, 15],
  popupAnchor: [0, -15],
});

// Highlight marker used when SI focuses a specific address.
const focusIcon = new L.DivIcon({
  className: 'focus-marker-icon',
  html: `<div class="focus-pulse"></div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
  popupAnchor: [0, -14],
});

// Format a number as USD (or "—" when null/undefined)
const fmtMoney = (v) =>
  typeof v === 'number' && Number.isFinite(v)
    ? v.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
    : '—';

// Centers the map on (and opens the popup of) the address SI asked to focus.
// Re-runs whenever the focused point changes.
function FocusMarker({ focus }) {
  const map = useMap();
  const markerRef = useRef(null);
  const lat = focus?.latitude;
  const lng = focus?.longitude;
  const sig = focus ? `${focus.melissaAddressKey || ''}|${lat},${lng}` : '';

  useEffect(() => {
    if (lat == null || lng == null) return undefined;
    map.setView([lat, lng], Math.max(map.getZoom() || 0, 16));
    const t = setTimeout(() => {
      try {
        markerRef.current?.openPopup();
      } catch {
        /* ignore */
      }
    }, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig]);

  if (lat == null || lng == null) return null;

  const m = focus.metrics || {};
  const hasMetrics = focus.metrics != null;

  return (
    <Marker position={[lat, lng]} icon={focusIcon} ref={markerRef} zIndexOffset={1000}>
      <Popup>
        <div className="address-popup">
          <strong>{focus.addressLine1 || focus.name || 'Selected point'}</strong>
          <span className="focus-badge">Focused</span>
          {focus.addressLine2 && <><br />{focus.addressLine2}</>}
          {(focus.city || focus.state || focus.zipCode) && (
            <><br />{[focus.city, focus.state].filter(Boolean).join(', ')} {focus.zipCode || ''}</>
          )}
          {focus.formattedAddress && !focus.addressLine1 && (
            <><br />{focus.formattedAddress}</>
          )}
          {typeof focus.distance === 'number' && (
            <><br /><span className="distance-info">{focus.distance.toFixed(2)} miles</span></>
          )}
          {focus.suiteCount > 0 && (
            <span className="apt-badge">APT ({focus.suiteCount})</span>
          )}
          {hasMetrics && (
            <div className="customer-metrics">
              <div>ARO: {fmtMoney(m.aro)}</div>
              <div>Total spent: {fmtMoney(m.totalSpent)}</div>
              <div>Visits: {Number.isFinite(m.visitsCount) ? m.visitsCount : '—'}</div>
            </div>
          )}
        </div>
      </Popup>
    </Marker>
  );
}

// Component to recenter map when center coordinates actually change.
// Skips the setView if the new center is already within the current viewport,
// so dragging the shop marker doesn't reset the user's zoom level.
function MapRecenter({ center, zoom }) {
  const map = useMap();
  const prevCenterRef = useRef(null);

  useEffect(() => {
    if (!center) return;

    const prevCenter = prevCenterRef.current;
    const centerChanged = !prevCenter ||
      prevCenter[0] !== center[0] ||
      prevCenter[1] !== center[1];

    if (!centerChanged) return;

    prevCenterRef.current = center;

    // If the new center is already visible, don't recenter — preserves user's zoom.
    const bounds = map.getBounds();
    if (bounds && bounds.contains(center)) return;

    map.setView(center, zoom);
  }, [center, zoom, map]);

  return null;
}

// Fits the map viewport to a set of [lat, lng] points (e.g. all the
// addresses of a campaign, or every marker on the shop map). Re-fits ONLY when
// the set of points actually changes — panning, zooming or selecting markers
// don't change the signature, so the user's manual zoom is preserved until new
// data arrives (a search, an SSE push, etc.).
function FitBounds({ points, maxZoom = 16, once = false }) {
  const map = useMap();
  const didFitRef = useRef(false);
  const signature = points.map((p) => `${p[0]},${p[1]}`).join('|');

  useEffect(() => {
    if (!points || points.length === 0) return;
    // `once`: frame the points only the FIRST time they appear, then never
    // again — later searches / circle moves keep the user's current viewport.
    if (once && didFitRef.current) return;

    if (points.length === 1) {
      map.setView(points[0], 14);
    } else {
      const bounds = L.latLngBounds(points);
      if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: [50, 50], maxZoom });
      }
    }
    didFitRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, map]);

  return null;
}

function ShopMap({
  shop,
  addresses = [],
  batchAddresses = [],
  customerAddresses = [],
  searchArea = null,
  onSearchAreaChange,
  searchCenter = null,
  searchRadius = null,
  focusAddress = null,
  height = '400px',
  fitToAddresses = false,
  fitToAll = false,
  showCustomers = true,
  showLegend = true,
}) {
  // Default center (US)
  const defaultCenter = [39.8283, -98.5795];
  const defaultZoom = 4;

  const hasShopLocation = shop?.latitude && shop?.longitude;
  const hasSearchArea = searchArea?.latitude != null && searchArea?.longitude != null;
  const hasSearchCenter = searchCenter?.latitude && searchCenter?.longitude;

  // The search-AREA center (movable). Priority: explicitly dragged area >
  // location-search center > the shop's own location. The SHOP marker itself is
  // always fixed at the shop coords (below); this is only the coverage circle.
  const areaCenter = hasSearchArea
    ? [searchArea.latitude, searchArea.longitude]
    : hasSearchCenter
      ? [searchCenter.latitude, searchCenter.longitude]
      : hasShopLocation
        ? [shop.latitude, shop.longitude]
        : null;

  // Local position for the draggable search-area handle (follows drag live).
  const [areaPosition, setAreaPosition] = useState(areaCenter);
  useEffect(() => {
    if (areaCenter) setAreaPosition(areaCenter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areaCenter?.[0], areaCenter?.[1]]);

  const radiusMilesEff = searchRadius || shop?.radiusMiles || 0;

  // Viewport center + the (draggable) circle center.
  const mapCenter = areaCenter || (hasShopLocation ? [shop.latitude, shop.longitude] : defaultCenter);
  const zoom = areaCenter || hasShopLocation ? 14 : defaultZoom;
  const circleCenter = areaPosition || areaCenter;
  const radiusMeters = areaCenter ? radiusMilesEff * 1609.34 : 0;
  const areaDraggable = !!onSearchAreaChange && !!areaCenter;
  // Only show the area handle when it's draggable or the area was actually moved
  // off the shop (drag/location search). On read-only maps (e.g. campaign) we
  // still draw the circle but skip the handle so it doesn't sit on the shop pin.
  const showAreaHandle = !!areaCenter && (areaDraggable || hasSearchArea || hasSearchCenter);

  return (
    <div className="shop-map-container" style={{ height }}>
      <MapContainer
        center={mapCenter}
        zoom={zoom}
        className="shop-map"
        scrollWheelZoom={true}
      >
        {/* Only two base layers by decision: Street (default) and Hybrid.
            Satellite and Terrain were removed. */}
        <LayersControl position="topright">
          <BaseLayer checked name="Street">
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              maxZoom={19}
            />
          </BaseLayer>
          <BaseLayer name="Hybrid">
            <LayerGroup>
              <TileLayer
                attribution='Tiles &copy; Esri'
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                maxZoom={19}
              />
              <TileLayer
                url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
                maxZoom={19}
              />
            </LayerGroup>
          </BaseLayer>
        </LayersControl>

        {/* MapRecenter keeps the viewport tracking the center on the campaign
            map. On the shop map (fitToAll) we DON'T recenter on updates — the
            initial FitBounds frames everything once and then the user's
            pan/zoom is preserved across searches / circle moves. */}
        {!fitToAll && <MapRecenter center={mapCenter} zoom={zoom} />}

        {/* Fit viewport to all address points (e.g. campaign map view) */}
        {fitToAddresses && (
          <FitBounds
            points={[...batchAddresses, ...addresses]
              .filter((a) => a.latitude && a.longitude)
              .map((a) => [a.latitude, a.longitude])}
          />
        )}

        {/* Fit viewport to EVERY marker on the shop map ONCE on initial load:
            shop + customers + batch + results. After that, new searches and
            circle moves keep the current viewport (no zoom-out). */}
        {fitToAll && (
          <FitBounds
            once
            points={[
              ...(hasShopLocation ? [{ latitude: shop.latitude, longitude: shop.longitude }] : []),
              ...batchAddresses,
              ...customerAddresses,
              ...addresses,
            ]
              .filter((a) => a.latitude && a.longitude)
              .map((a) => [a.latitude, a.longitude])}
          />
        )}

        {/* Search radius circle — same blue as the search-area handle it's
            centered on, so "search area" reads as ONE object. It used to be
            yellow, which both clashed with the yellow "in batch" dots and
            washed out over light street tiles. */}
        {radiusMeters > 0 && (
          <Circle
            center={circleCenter}
            radius={radiusMeters}
            pathOptions={{
              color: '#3d59a8',
              fillColor: '#3d59a8',
              fillOpacity: 0.1,
              weight: 3,
              dashArray: '6, 5',
            }}
          />
        )}

        {/* Draggable SEARCH-AREA handle — the movable center of the coverage
            circle. Dragging it relocates the search area (the shop stays put).
            On drop, the parent decides what to do (e.g. re-run the search). */}
        {showAreaHandle && (
          <Marker
            position={areaPosition || areaCenter}
            icon={searchAreaIcon}
            draggable={areaDraggable}
            zIndexOffset={500}
            eventHandlers={
              areaDraggable
                ? {
                    drag: (e) => {
                      const { lat, lng } = e.target.getLatLng();
                      setAreaPosition([lat, lng]);
                    },
                    dragend: (e) => {
                      const { lat, lng } = e.target.getLatLng();
                      setAreaPosition([lat, lng]);
                      onSearchAreaChange?.({ latitude: lat, longitude: lng });
                    },
                  }
                : undefined
            }
          >
            <Popup>
              <div className="shop-popup">
                <strong>Search area</strong>
                <br />
                <span className="radius-info">Radius: {radiusMilesEff} miles</span>
                {hasSearchCenter && (searchCenter.city || searchCenter.zipCode) && (
                  <>
                    <br />
                    {searchCenter.formattedAddress ||
                      `${searchCenter.city || ''} ${searchCenter.state || ''} ${searchCenter.zipCode || ''}`.trim()}
                  </>
                )}
                {areaDraggable && (
                  <>
                    <br />
                    <span className="drag-hint">Drag to move the search area</span>
                  </>
                )}
              </div>
            </Popup>
          </Marker>
        )}

        {/* Shop marker — ALWAYS fixed at the geocoded shop location (not
            draggable). Only the search area above moves. */}
        {hasShopLocation && (
          <Marker position={[shop.latitude, shop.longitude]} icon={shopIcon}>
            <Popup>
              <div className="shop-popup">
                <strong>{shop.shopName}</strong>
                <br />
                {shop.address}
                <br />
                {shop.city}, {shop.state} {shop.zipCode}
              </div>
            </Popup>
          </Marker>
        )}

        {/* Batch address markers (from previous batches) */}
        {batchAddresses.map((address, index) => {
          if (!address.latitude || !address.longitude) return null;

          // Skip if this address is in current search results
          const inSearchResults = addresses.some(
            (a) => a.melissaAddressKey === address.melissaAddressKey
          );
          if (inSearchResults) return null;

          const icon = createAddressIcon('batch');

          return (
            <Marker
              key={`batch-${address.melissaAddressKey || index}`}
              position={[address.latitude, address.longitude]}
              icon={icon}
            >
              <Popup>
                <div className="address-popup">
                  <strong>{address.addressLine1}</strong>
                  {address.addressLine2 && <><br />{address.addressLine2}</>}
                  <br />
                  {address.city}, {address.state} {address.zipCode}
                  <br />
                  <span className="distance-info">
                    {address.distance?.toFixed(2)} miles
                  </span>
                  {address.suiteCount > 0 && (
                    <span className="apt-badge">APT ({address.suiteCount})</span>
                  )}
                  <span className="batch-badge">
                    Batch: {address.batches?.[0]?.batchNumber || 'N/A'}
                  </span>
                </div>
              </Popup>
            </Marker>
          );
        })}

        {/* Existing-customer markers (pushed by Service Intel) — toggleable */}
        {showCustomers && customerAddresses.map((customer, index) => {
          if (!customer.latitude || !customer.longitude) return null;

          const m = customer.metrics || {};

          return (
            <Marker
              key={`customer-${customer.shopCustomerID || index}`}
              position={[customer.latitude, customer.longitude]}
              icon={customerIcon}
            >
              <Popup>
                <div className="address-popup">
                  <strong>{customer.name || 'Existing customer'}</strong>
                  <span className="customer-badge">Customer</span>
                  {customer.formattedAddress && (
                    <>
                      <br />
                      {customer.formattedAddress}
                    </>
                  )}
                  <div className="customer-metrics">
                    <div>ARO: {fmtMoney(m.aro)}</div>
                    <div>Total spent: {fmtMoney(m.totalSpent)}</div>
                    <div>Last 12 mo: {fmtMoney(m.totalSpentLastYear)}</div>
                    <div>
                      Visits: {Number.isFinite(m.visitsCount) ? m.visitsCount : '—'}
                      {' · '}
                      Vehicles: {Number.isFinite(customer.vehicleCount) ? customer.vehicleCount : '—'}
                    </div>
                  </div>
                </div>
              </Popup>
            </Marker>
          );
        })}

        {/* Search result address markers */}
        {addresses.map((address, index) => {
          if (!address.latitude || !address.longitude) return null;

          const type = address.isPreviouslyUsed ? 'used' : 'new';
          const icon = createAddressIcon(type);

          return (
            <Marker
              key={address.melissaAddressKey || index}
              position={[address.latitude, address.longitude]}
              icon={icon}
            >
              <Popup>
                <div className="address-popup">
                  <strong>{address.addressLine1}</strong>
                  {address.addressLine2 && <><br />{address.addressLine2}</>}
                  <br />
                  {address.city}, {address.state} {address.zipCode}
                  <br />
                  <span className="distance-info">
                    {address.distance?.toFixed(2)} miles
                  </span>
                  {address.suiteCount > 0 && (
                    <span className="apt-badge">APT ({address.suiteCount})</span>
                  )}
                  {address.isPreviouslyUsed && (
                    <span className="used-badge">Already used</span>
                  )}
                </div>
              </Popup>
            </Marker>
          );
        })}

        {/* SI-requested focus: center + open popup on a specific point */}
        {focusAddress && <FocusMarker focus={focusAddress} />}
      </MapContainer>

      {/* Map legend — always shown (forced on across all views; the
          showLegend prop is intentionally ignored). */}
      <div className="map-legend">
        <div className="legend-item">
          <div className="legend-dot shop-dot"></div>
          <span>Shop</span>
        </div>
        <div className="legend-item">
          <div className="legend-dot batch-dot"></div>
          <span>In Batch</span>
        </div>
        <div className="legend-item">
          <div className="legend-dot new-dot"></div>
          <span>New</span>
        </div>
        <div className="legend-item">
          <div className="legend-dot used-dot"></div>
          <span>Used</span>
        </div>
        {showCustomers && (
          <div className="legend-item">
            <div className="legend-dot customer-dot"></div>
            <span>Customer</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default ShopMap;
